import { useCallback, useEffect, useMemo, useState } from "react";
import api, { fmtMoney, fmtNum } from "@/lib/api";
import { useWS } from "@/contexts/WebSocketContext";
import { toast } from "sonner";
import { FileCsv, CalendarBlank, MagnifyingGlass } from "@phosphor-icons/react";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";
import RefreshButton from "@/components/layout/RefreshButton";
import AccountIdentity from "@/components/AccountIdentity";
import GroupBadge from "@/components/GroupBadge";
import { downloadAccountsCsv, downloadHistoryCsv } from "@/lib/reports";

function ReportCard({ row }) {
  const maxDd = row.max_drawdown_today ?? 0;
  const profit = row.profit_today ?? 0;
  const withdrawals = row.withdrawals_today ?? 0;
  return (
    <div className="border border-app-border bg-app-surface p-4" data-testid={`report-card-${row.account_number}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <AccountIdentity
          group={row.group}
          groupColor={row.group_color}
          accountNumber={row.account_number}
          accountName={row.account_name}
          broker={row.broker}
          showBroker
        />
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-app-muted font-mono-d mb-1">Opening Balance</div>
          <div className="font-mono-d tabular-nums">{fmtMoney(row.opening_balance)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-app-muted font-mono-d mb-1">Closing Balance</div>
          <div className="font-mono-d tabular-nums">{fmtMoney(row.closing_balance)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-app-muted font-mono-d mb-1">Max DD Today</div>
          <div className={`font-mono-d tabular-nums ${maxDd > 8 ? "text-[#FF3B30]" : ""}`}>{fmtNum(maxDd)}%</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-app-muted font-mono-d mb-1">Profit (Today)</div>
          <div className={`font-mono-d tabular-nums ${profit >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>{fmtMoney(profit)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-app-muted font-mono-d mb-1">Withdrawals</div>
          <div className="font-mono-d tabular-nums text-[#F59E0B]">{fmtMoney(withdrawals)}</div>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const { registrationTick, liveMetrics, liveVersion } = useWS();
  const todayStr = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [isToday, setIsToday] = useState(true);
  const [reportDate, setReportDate] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [groups, setGroups] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: grp }] = await Promise.all([
        api.get("/reports/accounts", { params: { date: selectedDate } }),
        api.get("/groups"),
      ]);
      setReportDate(data.report_date || selectedDate);
      setIsToday(!!data.is_today);
      setRows(data.rows || []);
      setGroups(grp || []);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => { load(); }, [registrationTick, load]);

  const displayRows = useMemo(() => {
    if (!isToday) return rows;
    return rows.map((row) => {
      const live = liveMetrics[row.account_id];
      if (!live) return row;
      return {
        ...row,
        closing_balance: live.balance ?? row.closing_balance,
        profit_today: live.profit_today ?? row.profit_today,
        withdrawals_today: live.withdrawals_today ?? row.withdrawals_today,
      };
    });
  }, [rows, liveMetrics, liveVersion, isToday]);

  const filteredRows = useMemo(() => {
    let list = displayRows;
    if (search) {
      list = list.filter((r) =>
        `${r.account_number} ${r.broker} ${r.account_name} ${r.group || ""}`.toLowerCase().includes(search.toLowerCase()),
      );
    }
    if (groupFilter === "all") return list;
    if (groupFilter === "none") return list.filter((r) => !r.group);
    return list.filter((r) => r.group === groupFilter);
  }, [displayRows, groupFilter, search]);

  const handleExport = async (kind) => {
    if (exporting) return;
    setExporting(kind);
    try {
      if (kind === "today") await downloadAccountsCsv();
      else await downloadHistoryCsv();
      toast.success(kind === "today" ? "Accounts CSV downloaded" : "History CSV downloaded");
    } catch {
      toast.error("CSV export failed");
    } finally {
      setExporting(null);
    }
  };

  return (
    <PageShell>
      <PageHeader eyebrow="Fleet Tracking" title="REPORTS">
        <div className="flex flex-col items-stretch sm:items-end gap-2.5 w-full lg:w-auto">
          {reportDate && (
            <span className="inline-flex self-end items-center gap-1.5 px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border border-app-border text-app-subtle bg-app-surface">
              <CalendarBlank size={12} />
              {isToday ? "Today" : "Archive"} · {reportDate}
            </span>
          )}
          <div className="flex flex-wrap items-center justify-end gap-1.5">
            <RefreshButton onRefresh={load} testId="reports-refresh-btn" />
            <button
              type="button"
              data-testid="export-accounts-csv"
              disabled={!!exporting || filteredRows.length === 0}
              onClick={() => handleExport("today")}
              className="btn-touch inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-mono-d uppercase tracking-widest border border-[#10B981]/40 text-[#10B981] bg-app-surface hover:bg-[#10B981]/10 disabled:opacity-50"
            >
              <FileCsv size={16} />
              {exporting === "today" ? "Exporting…" : "Export CSV"}
            </button>
            <button
              type="button"
              data-testid="export-history-csv"
              disabled={!!exporting}
              onClick={() => handleExport("history")}
              className="btn-touch inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-mono-d uppercase tracking-widest border border-app-border bg-app-surface hover:bg-app-hover disabled:opacity-50"
            >
              <CalendarBlank size={16} />
              {exporting === "history" ? "Exporting…" : "History CSV"}
            </button>
          </div>
        </div>
      </PageHeader>

      <div className="page-filter-bar mb-6">
        <div className="page-filter-field w-full sm:w-44 lg:w-48">
          <label htmlFor="reports-date" className="page-filter-label">Date</label>
          <input
            id="reports-date"
            type="date"
            max={todayStr}
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            data-testid="reports-date-filter"
            className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm min-h-[40px] focus:outline-none focus:border-app-accent font-mono-d"
          />
        </div>
        <div className="page-filter-field flex-1 min-w-0">
          <label htmlFor="reports-search" className="page-filter-label">Search</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-app-muted pointer-events-none" />
            <input
              id="reports-search"
              placeholder="Account, broker, name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-app-bg border border-app-border pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-app-accent min-h-[40px]"
            />
          </div>
        </div>
        <div className="page-filter-field w-full sm:w-44 lg:w-48">
          <label htmlFor="reports-group-filter" className="page-filter-label">Group</label>
          <select
            id="reports-group-filter"
            data-testid="reports-group-filter"
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="w-full bg-app-bg border border-app-border px-3 py-2 text-sm min-h-[40px] focus:outline-none focus:border-app-accent"
          >
            <option value="all">All groups</option>
            <option value="none">Unassigned</option>
            {groups.map((g) => (
              <option key={g.name} value={g.name}>{g.name}</option>
            ))}
          </select>
        </div>
        <div className="page-filter-field w-full sm:w-36 lg:w-40">
          <span className="page-filter-label">Showing</span>
          <div className="flex items-center min-h-[40px] px-3 border border-app-border bg-app-bg font-mono-d text-sm tabular-nums text-app-text">
            {filteredRows.length} account{filteredRows.length === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="mb-4 sm:mb-6 border border-app-border bg-app-surface p-4 sm:p-5">
        <div className="font-mono-d text-[10px] uppercase tracking-[0.2em] text-app-subtle mb-2">CSV columns (today export)</div>
        <p className="text-sm text-app-subtle leading-relaxed mb-3">
          All accounts with colored <span className="text-app-text">Group</span> tag,{" "}
          <span className="text-app-text">Opening Balance</span>,{" "}
          <span className="text-app-text">Closing Balance</span>, and{" "}
          <span className="text-app-text">Max DD Today (%)</span>,{" "}
          <span className="text-app-text">Profit (Today)</span>, and{" "}
          <span className="text-app-text">Withdrawals (Today)</span> — withdrawals are separated from trading P/L.
        </p>
        <div className="flex flex-wrap gap-2">
          {groups.map((g) => (
            <GroupBadge key={g.name} name={g.name} color={g.color} />
          ))}
        </div>
      </div>

      {loading && (
        <div className="font-mono-d text-xs text-app-muted uppercase tracking-widest">Loading report data…</div>
      )}

      {!loading && filteredRows.length === 0 && (
        <div className="border border-app-border bg-app-surface px-4 py-12 text-center text-app-muted font-mono-d text-xs">
          NO ACCOUNTS TO REPORT
        </div>
      )}

      {!loading && filteredRows.length > 0 && (
        <>
          <div className="lg:hidden space-y-3 mb-4">
            {filteredRows.map((row) => (
              <ReportCard key={row.account_id} row={row} />
            ))}
          </div>

          <div className="hidden lg:block border border-app-border bg-app-surface table-scroll">
            <table className="w-full min-w-[900px]" data-testid="reports-table">
              <thead>
                <tr className="border-b border-app-border text-left">
                  {["Account", "Broker", "Opening Balance", "Closing Balance", "Max DD Today", "Profit (Today)", "Withdrawals"].map((h) => (
                    <th key={h} className="px-3 py-3 font-mono-d text-[10px] uppercase tracking-widest text-app-subtle whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((row) => {
                  const maxDd = row.max_drawdown_today ?? 0;
                  const profit = row.profit_today ?? 0;
                  const withdrawals = row.withdrawals_today ?? 0;
                  return (
                    <tr key={row.account_id} className="border-b border-app-border/70 hover:bg-app-hover">
                      <td className="px-3 py-3">
                        <AccountIdentity
                          group={row.group}
                          groupColor={row.group_color}
                          accountNumber={row.account_number}
                          accountName={row.account_name}
                        />
                      </td>
                      <td className="px-3 py-3 text-sm text-app-subtle">{row.broker}</td>
                      <td className="px-3 py-3 font-mono-d text-sm tabular-nums">{fmtMoney(row.opening_balance)}</td>
                      <td className="px-3 py-3 font-mono-d text-sm tabular-nums">{fmtMoney(row.closing_balance)}</td>
                      <td className={`px-3 py-3 font-mono-d text-sm tabular-nums ${maxDd > 8 ? "text-[#FF3B30]" : ""}`}>
                        {fmtNum(maxDd)}%
                      </td>
                      <td className={`px-3 py-3 font-mono-d text-sm tabular-nums ${profit >= 0 ? "text-[#10B981]" : "text-[#FF3B30]"}`}>
                        {fmtMoney(profit)}
                      </td>
                      <td className="px-3 py-3 font-mono-d text-sm tabular-nums text-[#F59E0B]">
                        {fmtMoney(withdrawals)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="mt-4 font-mono-d text-[10px] text-app-muted uppercase tracking-widest">
            {filteredRows.length} account{filteredRows.length === 1 ? "" : "s"} · live closing balance updates via WebSocket
          </div>
        </>
      )}
    </PageShell>
  );
}
